import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import useUnsavedChangesWarning from '@/hooks/useUnsavedChangesWarning';
import {
  hasUnsaved,
  type SkillEdits,
  type UpdateSkillEdits,
} from './skillEdits';

type SkillEditsValue = { edits: SkillEdits; onEdits: UpdateSkillEdits };

const SkillEditsContext = createContext<SkillEditsValue | null>(null);

/**
 * Holds the unsaved skill-editor text for the whole app shell, so the chat
 * drawer's Agents view and the Agents page share drafts and survive each
 * other's unmounts. In memory only, never browser storage: a refused paste may
 * hold client data. Also owns the leave-the-page warning.
 */
export function SkillEditsProvider({ children }: { children: ReactNode }) {
  const [edits, onEdits] = useState<SkillEdits>({});
  useUnsavedChangesWarning(hasUnsaved(edits));
  const value = useMemo(() => ({ edits, onEdits }), [edits]);
  return (
    <SkillEditsContext.Provider value={value}>
      {children}
    </SkillEditsContext.Provider>
  );
}

export function useSkillEdits(): SkillEditsValue {
  const value = useContext(SkillEditsContext);
  if (!value) {
    throw new Error('useSkillEdits must be used inside SkillEditsProvider');
  }
  return value;
}
